import { useId, useLayoutEffect, useRef } from "react";
import Avatar from "boring-avatars";

/** Todas las variantes de boring-avatars (6). Debe coincidir con backend server.py AVATAR_VARIANTS. */
const AVATAR_VARIANTS = ["marble", "beam", "pixel", "sunset", "ring", "bauhaus"];
const DEFAULT_COLORS = ["#92A1C6", "#146A7C", "#F0AB3D", "#C271B4", "#C20D90"];

/** Paleta amplia para randomizar: variante + color dan muchas combinaciones. */
const COLOR_PALETTE = [
  "#3b82f6", "#8b5cf6", "#ec4899", "#ef4444", "#f97316", "#eab308", "#22c55e", "#14b8a6", "#06b6d4", "#6366f1",
  "#92A1C6", "#146A7C", "#F0AB3D", "#C271B4", "#C20D90", "#0ea5e9", "#a855f7", "#f43f5e", "#fb923c", "#84cc16",
  "#2dd4bf", "#38bdf8", "#818cf8", "#c084fc", "#f472b6", "#fb7185", "#fdba74", "#a3e635", "#4ade80", "#34d399",
];

/**
 * Paletas completas para randomizar (5 colores cada una). Se guarda en el usuario el primer color
 * (`color`), que identifica la paleta; colores antiguos siguen usando DEFAULT_COLORS.
 */
const AVATAR_PALETTES = [
  ["#264653", "#2a9d8f", "#e9c46a", "#f4a261", "#e76f51"],
  ["#e63946", "#f1faee", "#a8dadc", "#457b9d", "#1d3557"],
  ["#606c38", "#283618", "#fefae0", "#dda15e", "#bc6c25"],
  ["#cdb4db", "#ffc8dd", "#ffafcc", "#bde0fe", "#a2d2ff"],
  ["#003049", "#d62828", "#f77f00", "#fcbf49", "#eae2b7"],
  ["#8ecae6", "#219ebc", "#023047", "#ffb703", "#fb8500"],
  ["#f72585", "#7209b7", "#3a0ca3", "#4361ee", "#4cc9f0"],
  ["#ccd5ae", "#e9edc9", "#fefae0", "#faedcd", "#d4a373"],
  ["#ff595e", "#ffca3a", "#8ac926", "#1982c4", "#6a4c93"],
  ["#0b090a", "#161a1d", "#660708", "#a4161a", "#e5383b"],
  ["#2b2d42", "#8d99ae", "#edf2f4", "#ef233c", "#d90429"],
  ["#22223b", "#4a4e69", "#9a8c98", "#c9ada7", "#f2e9e4"],
  ["#ffbe0b", "#fb5607", "#ff006e", "#8338ec", "#3a86ff"],
  ["#390099", "#9e0059", "#ff0054", "#ff5400", "#ffbd00"],
  ["#355070", "#6d597a", "#b56576", "#e56b6f", "#eaac8b"],
  ["#0081a7", "#00afb9", "#fdfcdc", "#fed9b7", "#f07167"],
  ["#edae49", "#d1495b", "#00798c", "#30638e", "#003d5b"],
  ["#ef476f", "#ffd166", "#06d6a0", "#118ab2", "#073b4c"],
  ["#1a535c", "#4ecdc4", "#f7fff7", "#ff6b6b", "#ffe66d"],
  ["#5f0f40", "#9a031e", "#fb8b24", "#e36414", "#0f4c5c"],
  ["#d8e2dc", "#ffe5d9", "#ffcad4", "#f4acb7", "#9d8189"],
  ["#3d405b", "#81b29a", "#f2cc8f", "#e07a5f", "#f4f1de"],
  ["#10002b", "#240046", "#5a189a", "#9d4edd", "#e0aaff"],
  ["#006d77", "#83c5be", "#edf6f9", "#ffddd2", "#e29578"],
  ["#f94144", "#f3722c", "#f8961e", "#90be6d", "#577590"],
  ["#2d00f7", "#8900f2", "#bc00dd", "#e500a4", "#ff0a54"],
  ["#d9ed92", "#99d98c", "#52b69a", "#168aad", "#1e6091"],
  ["#7400b8", "#5e60ce", "#48bfe3", "#64dfdf", "#80ffdb"],
  ["#ff9f1c", "#ffbf69", "#ffffff", "#cbf3f0", "#2ec4b6"],
  ["#fec5bb", "#fcd5ce", "#fae1dd", "#e8e8e4", "#9a8f97"],
  ["#033270", "#1368aa", "#4091c9", "#f29479", "#f26a4f"],
  ["#582f0e", "#7f4f24", "#936639", "#a68a64", "#b6ad90"],
  ["#00296b", "#003f88", "#00509d", "#fdc500", "#ffd500"],
  ["#9b5de5", "#f15bb5", "#fee440", "#00bbf9", "#00f5d4"],
  ["#2f3e46", "#354f52", "#52796f", "#84a98c", "#cad2c5"],
  ["#e0fbfc", "#c2dfe3", "#9db4c0", "#5c6b73", "#253237"],
  ["#f6bd60", "#f7ede2", "#f5cac3", "#84a59d", "#f28482"],
  ["#0d1b2a", "#1b263b", "#415a77", "#778da9", "#e0e1dd"],
  ["#ffcdb2", "#ffb4a2", "#e5989b", "#b5838d", "#6d6875"],
  ["#540d6e", "#ee4266", "#ffd23f", "#3bceac", "#0ead69"],
];
const PALETTE_BY_KEY = Object.fromEntries(AVATAR_PALETTES.map((p) => [p[0].toLowerCase(), p]));

/**
 * Avatar por usuario (Boring Avatars). Acepta user { email, nombre?, color?, avatar_variant? } o props name, variant, color, size.
 */
export function UserAvatar({ user, name, variant, color, size = 40, className = "", square = false }) {
  const n = name ?? user?.email ?? user?.nombre ?? "";
  const v = variant ?? user?.avatar_variant ?? "marble";
  const c = color ?? user?.color;
  const colors =
    (c && PALETTE_BY_KEY[c.toLowerCase()]) || (c ? [c, ...DEFAULT_COLORS.filter((x) => x !== c)].slice(0, 5) : DEFAULT_COLORS);

  const wrapperRef = useRef(null);
  const uid = useId().replace(/[^a-zA-Z0-9_-]/g, "");

  // «sunset» usa degradados con id = nombre (email). Si el mismo usuario se pinta dos veces y la primera copia
  // está oculta (p. ej. lista móvil con md:hidden), todas apuntan a esa y el avatar sale vacío: ids únicos por instancia.
  useLayoutEffect(() => {
    if (v !== "sunset") return;
    const svg = wrapperRef.current?.querySelector("svg");
    if (!svg) return;
    const suffix = `__${uid}`;
    svg.querySelectorAll("linearGradient[id]").forEach((g) => {
      if (g.id.endsWith(suffix)) return;
      const oldRef = `url(#${g.id})`;
      g.id = `${g.id}${suffix}`;
      svg.querySelectorAll("[fill]").forEach((el) => {
        if (el.getAttribute("fill") === oldRef) el.setAttribute("fill", `url(#${g.id})`);
      });
    });
  });

  return (
    <span ref={wrapperRef} style={{ display: "contents" }}>
      <Avatar
        name={n}
        variant={v}
        size={size}
        colors={colors}
        square={square}
        className={className}
      />
    </span>
  );
}

export { AVATAR_VARIANTS, COLOR_PALETTE, AVATAR_PALETTES };
